import {
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react'

// const baseUrl = 'http://localhost:3001'
const baseUrl =
  'https://king-prawn-app-ugsdt.ondigitalocean.app'

const PUBLIC_ENDPOINTS = ['loginAdmin', 'registerAdmin']

/** Маршруты, где JWT не обязателен — не чистим acc и не редиректим на /auth при 401 */
function isRouteNoJwtRequired() {
  if (typeof window === 'undefined') return false
  const path = window.location.pathname || ''
  const noJwtPrefixes = [
    '/auth',
    '/admin/botTimer',
    '/juice',
    '/buyerList',
    '/client/static',
    '/admin/productsManager/history',
  ]
  return noJwtPrefixes.some(
    (prefix) => path === prefix || path.startsWith(prefix + '/'),
  )
}

const baseQueryWithAuth = async (
  args,
  api,
  extraOptions,
) => {
  const result = await fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { endpoint }) => {
      if (!PUBLIC_ENDPOINTS.includes(endpoint)) {
        try {
          const acc = JSON.parse(
            localStorage.getItem('acc') || '{}',
          )
          if (acc?.token) {
            headers.set(
              'Authorization',
              `Bearer ${acc.token}`,
            )
          }
        } catch (e) {
          // ignore
        }
      }
      return headers
    },
  })(args, api, extraOptions)

  if (result.error?.status === 401) {
    if (!isRouteNoJwtRequired()) {
      localStorage.removeItem('acc')
      localStorage.removeItem('site')
      if (
        typeof window !== 'undefined' &&
        window.location.pathname !== '/auth'
      ) {
        window.location.href = '/auth'
      }
    }
  }
  return result
}

export const digitalAppApi = createApi({
  reducerPath: 'digitalAppApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    //SUppliers credentials
    getSuppliersCredentials: builder.query({
      query: (query) => ({
        url: `/api/debtors-supp`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
        params: {
          limit: query.limit || 9999,
          search: query.search || '',
        },
      }),
      providesTags: (result, error, id) => [
        { type: 'SupCredentials', id: id },
      ],
    }),
    getSuppliersCredentialsById: builder.query({
      query: (id) => ({
        url: `/api/debtors-supp/${id}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
      }),
      providesTags: (result, error, id) => [
        { type: 'SupCredentials', id: id },
      ],
    }),
    updateSuppliersCredentialsById: builder.mutation({
      query: (body) => ({
        url: `/api/debtors-supp/${body.id}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
        body: body.data,
      }),
      invalidatesTags: ['SupCredentials'],
    }),
    deleteSuppliersCredentialsById: builder.mutation({
      query: (id) => ({
        url: `/api/debtors-supp/${id}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'DELETE',
      }),
      invalidatesTags: ['SupCredentials'],
    }),
    CreateSuppliersCredentials: builder.mutation({
      query: (body) => ({
        url: '/api/debtors-supp',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SupCredentials'],
    }),

    // GET /api/orders/stats — статистика заказов с теми же фильтрами, что и список
    getOrdersStats: builder.query({
      query: (query) => ({
        url: '/api/orders/stats',
        method: 'GET',
        params: {
          tradeName: query.tradeName || localStorage.getItem('name') || 'TPO',
          limit: query.limit || 500,
          search: query.search || '',
          status: query.status
            ? (Array.isArray(query.status)
                ? query.status.map((obj) => obj._id).join(',')
                : query.status)
            : '',
          platform: query.platform || '',
          tradeNames: Array.isArray(query.tradeNames)
            ? query.tradeNames.join(',')
            : query.tradeNames || '',
          deliveryDate: query.deliveryDate || '',
          month: query.month || '',
          simpleDeliveryDate: query.simpleDeliveryDate || '',
          stockStatus: query.stockStatus || '',
        },
      }),
      providesTags: (result, error, arg) => [{ type: 'OrdersStats', id: arg?.tradeName ?? 'list' }],
    }),

    getMeetings: builder.query({
      query: (query) => ({
        url: `/api/meetings`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
        params: {
          limit: query.limit || 9999,
          search: query.search || '',
        },
      }),
      providesTags: (result, error, id) => ['Meetings'],
    }),
    getMeetingsById: builder.query({
      query: (id) => ({
        url: `/api/meetings/${id}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
      }),
      providesTags: (result, error, id) => ['Meetings'],
    }),
    updateMeetingsById: builder.mutation({
      query: (body) => ({
        url: `/api/meetings/${body.id}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
        body: body.data,
      }),
      invalidatesTags: ['Meetings'],
    }),
    deleteMeetingsById: builder.mutation({
      query: (id) => ({
        url: `/api/meetings/${id}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'DELETE',
      }),
      invalidatesTags: ['Meetings'],
    }),
    CreateMeetings: builder.mutation({
      query: (body) => ({
        url: '/api/meetings',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Meetings'],
    }),

    getAlbaranById: builder.query({
      query: (params) => {
        const { id, tradeName } = params || {}
        const queryParams = new URLSearchParams()

        if (tradeName)
          queryParams.append('tradeName', tradeName)

        const queryString = queryParams.toString()
        const url = `/api/print-document/albaran/${id}${
          queryString ? `?${queryString}` : ''
        }`

        return {
          url,
          headers: {
            'Content-Type': 'application/pdf',
          },
          method: 'GET',
          responseHandler: async (response) => {
            if (!response.ok) {
              // Читаем текст ошибки
              throw new Error(
                (await response.text()) ||
                  `HTTP error! Status: ${response.status}`,
              )
            }
            // 💡 ЭТО КРИТИЧНО: Если 200 OK, возвращаем Blob
            return response.blob()
          },
        }
      },
    }),
    getCheckListById: builder.query({
      query: (params) => {
        const { id, tradeName } = params || {}
        const queryParams = new URLSearchParams()

        if (tradeName)
          queryParams.append('tradeName', tradeName)

        const queryString = queryParams.toString()
        const url = `/api/print-document/checklist/${id}${
          queryString ? `?${queryString}` : ''
        }`

        return {
          url,
          headers: {
            'Content-Type': 'application/pdf',
          },
          method: 'GET',
          responseHandler: async (response) => {
            if (!response.ok) {
              // Читаем текст ошибки
              throw new Error(
                (await response.text()) ||
                  `HTTP error! Status: ${response.status}`,
              )
            }
            // 💡 ЭТО КРИТИЧНО: Если 200 OK, возвращаем Blob
            return response.blob()
          },
        }
      },
    }),
    getPersonalSalesPdf: builder.query({
      query: (params) => {
        const { tradeName } = params || {}
        const queryParams = new URLSearchParams()
        if (tradeName)
          queryParams.append('tradeName', tradeName)
        const queryString = queryParams.toString()
        const url = `/api/print-document/personal-sales${
          queryString ? `?${queryString}` : ''
        }`
        return {
          url,
          headers: {
            'Content-Type': 'application/pdf',
          },
          method: 'GET',
          responseHandler: async (response) => {
            if (!response.ok) {
              throw new Error(
                (await response.text()) ||
                  `HTTP error! Status: ${response.status}`,
              )
            }
            return response.blob()
          },
        }
      },
    }),
    getInvoiceById: builder.query({
      query: (params) => {
        const {
          orderId,
          accountKey,
          number,
          numberInYear,
          orderIds,
          date,
          tradeName,
        } = params || {}
        const queryParams = new URLSearchParams()

        if (accountKey)
          queryParams.append('accountKey', accountKey)
        if (number) queryParams.append('number', number)
        if (numberInYear)
          queryParams.append('numberInYear', numberInYear)
        if (orderIds)
          queryParams.append('orderIds', orderIds)
        if (date) queryParams.append('date', date)
        if (tradeName)
          queryParams.append('tradeName', tradeName)

        const queryString = queryParams.toString()
        const url = orderId
          ? `/api/print-document/invoice/${orderId}${queryString ? `?${queryString}` : ''}`
          : `/api/print-document/invoice/any${queryString ? `?${queryString}` : ''}`

        return {
          url,
          headers: {
            'Content-Type': 'application/pdf',
          },
          method: 'GET',
          responseHandler: async (response) => {
            if (!response.ok) {
              // Читаем текст ошибки
              throw new Error(
                (await response.text()) ||
                  `HTTP error! Status: ${response.status}`,
              )
            }
            // 💡 ЭТО КРИТИЧНО: Если 200 OK, возвращаем Blob
            return response.blob()
          },
        }
      },
    }),
    getInvoiceDetailed: builder.query({
      query: (params) => {
        const {
          accountKey,
          orderIds,
          number,
          numberInYear,
          date,
          tradeName,
        } = params || {}
        const queryParams = new URLSearchParams()

        if (accountKey)
          queryParams.append('accountKey', accountKey)
        if (orderIds)
          queryParams.append('orderIds', orderIds)
        if (number) queryParams.append('number', number)
        if (numberInYear)
          queryParams.append('numberInYear', numberInYear)
        if (date) queryParams.append('date', date)
        if (tradeName)
          queryParams.append('tradeName', tradeName)

        const queryString = queryParams.toString()

        return {
          url: `/api/print-document/invoice-detailed${queryString ? `?${queryString}` : ''}`,
          headers: {
            'Content-Type': 'application/pdf',
          },
          method: 'GET',
          responseHandler: async (response) => {
            if (!response.ok) {
              // Читаем текст ошибки
              throw new Error(
                (await response.text()) ||
                  `HTTP error! Status: ${response.status}`,
              )
            }
            // 💡 ЭТО КРИТИЧНО: Если 200 OK, возвращаем Blob
            return response.blob()
          },
        }
      },
    }),
    updateContactStatus: builder.mutation({
      query: (body) => ({
        url: `/api/contacts/${body.id}/status`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
        body: {
          status: body.status,
        },
      }),
    }),
    deleteDatabaseStatus: builder.mutation({
      query: (body) => ({
        url: `/api/statuses/${body.id}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'DELETE',
      }),
    }),

    // CONTACTS

    // Получить список контактов (с фильтрами через params)

    getContacts: builder.query({
      query: (params) => ({
        url: `/api/contacts`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((c) => ({
                type: 'Contacts',
                id: c._id,
              })),
              { type: 'Contacts', id: 'LIST' },
            ]
          : [{ type: 'Contacts', id: 'LIST' }],
    }),

    // Получить контакт по id
    getContactById: builder.query({
      query: (id) => ({
        url: `/api/contacts/${id}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      // ВАЖНО: добавляем общий LIST‑тег, чтобы можно было
      // рефетчить этот запрос из мутаций (Marco / Personal)
      providesTags: (result, error, id) => [
        { type: 'Contacts', id },
        { type: 'Contacts', id: 'LIST' },
      ],
    }),

    // Создать контакт
    createContact: builder.mutation({
      query: (body) => ({
        url: `/api/contacts`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }),
      invalidatesTags: [{ type: 'Contacts', id: 'LIST' }],
    }),

    // Обновить контакт
    updateContact: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/contacts/${id}`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Contacts', id },
        { type: 'Contacts', id: 'LIST' },
      ],
    }),

    // Удалить контакт
    deleteContact: builder.mutation({
      query: (id) => ({
        url: `/api/contacts/${id}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Contacts', id },
        { type: 'Contacts', id: 'LIST' },
      ],
    }),

    // Получить контакт по tradeName
    getContactByTradeName: builder.query({
      query: (tradeName) => ({
        url: `/api/contacts/trade-name`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        params: { tradeName },
      }),
      providesTags: (result) =>
        result?.data?._id
          ? [{ type: 'Contacts', id: result.data._id }]
          : [],
    }),

    // Получить контакт по email
    getContactByEmail: builder.query({
      query: (email) => ({
        url: `/api/contacts/email`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        params: { email },
      }),
      providesTags: (result) =>
        result?.data?._id
          ? [{ type: 'Contacts', id: result.data._id }]
          : [],
    }),

    // Получить контакт по phone
    getContactByPhone: builder.query({
      query: (phone) => ({
        url: `/api/contacts/phone`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        params: { phone },
      }),
      providesTags: (result) =>
        result?.data?._id
          ? [{ type: 'Contacts', id: result.data._id }]
          : [],
    }),

    // Получить список типов контактов
    getContactTypes: builder.query({
      query: () => ({
        url: `/api/contacts/types`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    // Сменить статус контакта
    changeContactStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/api/contacts/${id}/status`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Contacts', id },
        { type: 'Contacts', id: 'LIST' },
      ],
    }),

    // Получить статистику контактов
    getContactsStatistics: builder.query({
      query: () => ({
        url: `/api/contacts/statistics`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    getContactStatisticsByDate: builder.query({
      query: ({ contactId, startDate, endDate }) => {
        const params = new URLSearchParams()
        if (contactId) params.append('contactId', contactId)
        if (startDate) params.append('startDate', startDate)
        if (endDate) params.append('endDate', endDate)

        return {
          url: `/api/contacts-statistics/by-date?${params.toString()}`,
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'GET',
        }
      },
      providesTags: (result, error, { contactId }) => [
        { type: 'ContactStatisticsByDate', id: contactId },
      ],
    }),

    ////VACATION
    getVacations: builder.query({
      query: (query) => ({
        url: `/api/vacations`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
        params: {
          limit: query.limit || 9999,
          search: query.search || '',
        },
      }),
      providesTags: (result, error, id) => ['Vacation'],
    }),
    getVacationById: builder.query({
      query: (id) => ({
        url: `/api/vacations/${id}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
      }),
      providesTags: (result, error, id) => ['Vacation'],
    }),
    updateVacationById: builder.mutation({
      query: (body) => ({
        url: `/api/vacations/${body.id}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
        body: body.data,
      }),
      invalidatesTags: ['Vacation'],
    }),
    deleteVacationById: builder.mutation({
      query: (id) => ({
        url: `/api/vacations/${id}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'DELETE',
      }),
      invalidatesTags: ['Vacation'],
    }),
    CreateVacation: builder.mutation({
      query: (body) => ({
        url: '/api/vacations',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Vacation'],
    }),

    // CATALOG
    getCatalog: builder.query({
      query: (params) => ({
        url: `/api/catalog`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        params: {
          limit: params?.limit || 50,
          search: params?.search || '',
          sku: params?.sku || '',
        },
      }),
      providesTags: ['Catalog'],
    }),

    getCatalogBySku: builder.query({
      query: (sku) => ({
        url: `/api/catalog/sku`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        params: { sku },
      }),
      providesTags: (result, error, sku) => [
        { type: 'Catalog', id: sku },
      ],
    }),

    getCatalogMacro: builder.query({
      query: (query) => ({
        url: `/api/catalog/macro`,
        method: 'GET',
        params: {
          search: query.search || '',
        },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    // MARCO PERSONAL
    // GET /api/contacts/marco-personal?tradeName=BULBA
    // Находит контакт по tradeName
    // Возвращает macroPersonal (или null, если отсутствует)
    getMarcoPersonal: builder.query({
      query: (tradeName) => ({
        url: `/api/contacts/marco-personal`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        params: { tradeName },
      }),
      providesTags: (result, error, tradeName) => [
        { type: 'MarcoPersonal', id: tradeName },
      ],
    }),

    // GET /marco-personal/supplier-tradenames - массив tradeName поставщиков с macroPersonal
    // Сервер возвращает { success, data: string[] }
    getMarcoPersonalSupplierTradenames: builder.query({
      query: () => ({
        url: `/api/contacts/marco-personal/supplier-tradenames`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response) => {
        const list = response?.data
        return Array.isArray(list) ? list : []
      },
    }),

    // GET /api/factura-history/threshold-check?accountKey=...&clientCifOrNif=...&amountToAdd=...
    getFacturaHistoryThresholdCheck: builder.query({
      query: ({ accountKey, clientCifOrNif, amountToAdd }) => ({
        url: `/api/factura-history/threshold-check`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        params: { accountKey, clientCifOrNif, amountToAdd },
      }),
    }),

    // GET /api/factura-history/limit-report?year=...
    getFacturaHistoryLimitReport: builder.query({
      query: ({ year }) => ({
        url: `/api/factura-history/limit-report`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        params: { year },
      }),
    }),

    // POST /api/contacts/marco-personal?tradeName=BULBA
    // Находит контакт по tradeName
    // Сохраняет macroPersonal из тела запроса
    // Объект может содержать продукты
    addMarcoPersonal: builder.mutation({
      query: ({ tradeName, macroPersonal }) => {
        const queryParams = new URLSearchParams({
          tradeName,
        })
        return {
          url: `/api/contacts/marco-personal?${queryParams.toString()}`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: macroPersonal,
        }
      },
      // После изменения MarcoPersonal рефетчим и сам список Marco,
      // и все запросы, которые завязаны на Contacts LIST (в т.ч. getContactById)
      invalidatesTags: (result, error, { tradeName }) => [
        { type: 'MarcoPersonal', id: tradeName },
        { type: 'Contacts', id: 'LIST' },
      ],
    }),

    // POST /api/admin-base/makro-images/by-codes
    // Body: { codes: string[] }
    // Response: 200, array of { code, imageUrl, url } per requested code
    postMakroImagesByCodes: builder.mutation({
      query: (body) => ({
        url: '/api/admin-base/makro-images/by-codes',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }),
    }),

    // PERSONAL SALES
    // GET /api/contacts/personal-sales?tradeName=BULBA
    // Находит контакт по tradeName
    // Возвращает personalSales (или null, если отсутствует)
    getPersonalSales: builder.query({
      query: (tradeName) => ({
        url: `/api/contacts/personal-sales`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        params: { tradeName },
      }),
      providesTags: (result, error, tradeName) => [
        { type: 'PersonalSales', id: tradeName },
      ],
    }),

    // POST /api/contacts/personal-sales?tradeName=BULBA
    // Находит контакт по tradeName
    // Сохраняет personalSales из тела запроса
    // Объект может содержать продукты
    addPersonalSales: builder.mutation({
      query: ({ tradeName, personalSales }) => {
        const queryParams = new URLSearchParams({
          tradeName,
        })
        return {
          url: `/api/contacts/personal-sales?${queryParams.toString()}`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: personalSales,
        }
      },
      // Аналогично: обновляем персональные ставки и дергаем все запросы с Contacts LIST
      invalidatesTags: (result, error, { tradeName }) => [
        { type: 'PersonalSales', id: tradeName },
        { type: 'Contacts', id: 'LIST' },
      ],
    }),

    comparePersonalSalesWithCatalog: builder.mutation({
      query: ({ tradeName, items }) => ({
        url: '/api/contacts/personal-sales/compare-with-catalog',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: { tradeName, items },
      }),
    }),

    applyPersonalSalesPriceSync: builder.mutation({
      query: ({ tradeName, updates }) => ({
        url: '/api/contacts/personal-sales/apply-price-sync',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: { tradeName, updates },
      }),
      invalidatesTags: (result, error, { tradeName }) => [
        { type: 'PersonalSales', id: tradeName },
        { type: 'Contacts', id: 'LIST' },
      ],
    }),

    // Admin accounts (JWT: public and protected)
    loginAdmin: builder.mutation({
      query: (body) => ({
        url: `/api/auth`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body,
      }),
    }),
    registerAdmin: builder.mutation({
      query: (body) => ({
        url: `/api/createAccAdmin`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body,
      }),
    }),
    getUsers: builder.query({
      query: () => ({
        url: '/api/getUsers',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
      }),
      providesTags: ['Users'],
    }),
    createUser: builder.mutation({
      query: (userData) => ({
        url: '/api/createAccAdmin',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: { data: userData },
      }),
      invalidatesTags: ['Users'],
    }),
    updateUser: builder.mutation({
      query: ({ id, data }) => ({
        url: '/api/updateUser',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: { id, data },
      }),
      invalidatesTags: ['Users'],
    }),
    deleteUser: builder.mutation({
      query: (userId) => ({
        url: '/api/deleteUser',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: { id: userId },
      }),
      invalidatesTags: ['Users'],
    }),
    getUserById: builder.query({
      query: (userId) => ({
        url: `/api/getUser/${userId}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
      }),
      providesTags: (result, error, userId) => [
        { type: 'User', id: userId },
      ],
    }),
    getAdminAccById: builder.query({
      query: (id) => ({
        url: `/api/getAdminAccById/${id}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
      }),
    }),
    updatePaidAdminAcc: builder.mutation({
      query: (body) => ({
        url: '/api/updatePaidAdminAcc',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'PUT',
        body,
      }),
    }),
    updateTelegramDataAdminAcc: builder.mutation({
      query: (body) => ({
        url: '/api/updateTelegramDataAdminAcc',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body,
      }),
    }),

    //Buyer Codes

    getBuyerCodes: builder.query({
      query: () => ({
        url: `/api/buyer-codes`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
      }),
      providesTags: ['BuyerCode'],
    }),
    getBuyerCodeById: builder.query({
      query: (id) => ({
        url: `/api/buyer-codes/${id}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
      }),
      providesTags: ['BuyerCode'],
    }),
    createBuyerCode: builder.mutation({
      query: (body) => ({
        url: '/api/buyer-codes',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BuyerCode'],
    }),
    updateBuyerCodeById: builder.mutation({
      query: (id) => ({
        url: `/api/buyer-codes/${id}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
      }),
      invalidatesTags: ['BuyerCode'],
    }),
    deleteBuyerCodeById: builder.mutation({
      query: (id) => ({
        url: `/api/buyer-codes/${id}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'DELETE',
      }),
      invalidatesTags: ['BuyerCode'],
    }),

    // ===== Management (financial dashboard) =====
    // GET /api/management/state?year=...&siteName=...
    getManagementState: builder.query({
      query: ({ year, siteName } = {}) => ({
        url: '/api/management/state',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        params: {
          year,
          siteName: siteName || localStorage.getItem('name') || 'TPO',
        },
      }),
    }),
    // PUT /api/management/state — опционально (админ/бэкап); в UI сохранение только по строкам (PATCH/DELETE row)
    saveManagementState: builder.mutation({
      query: ({ year, siteName, sections }) => ({
        url: '/api/management/state',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          year,
          siteName: siteName || localStorage.getItem('name') || 'TPO',
          sections,
        },
      }),
    }),
    // PATCH /api/management/row — создать/обновить одну строку
    managementUpsertRow: builder.mutation({
      query: ({ year, siteName, sectionId, categoryId = null, row }) => ({
        url: '/api/management/row',
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          year,
          siteName: siteName || localStorage.getItem('name') || 'TPO',
          sectionId,
          categoryId,
          row,
        },
      }),
    }),
    // DELETE /api/management/row — удалить строку
    managementDeleteRow: builder.mutation({
      query: ({ year, siteName, sectionId, categoryId = null, rowId }) => ({
        url: '/api/management/row',
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          year,
          siteName: siteName || localStorage.getItem('name') || 'TPO',
          sectionId,
          categoryId,
          rowId,
        },
      }),
    }),
  }),
})
